import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MsalAuthenticationTemplate } from "@azure/msal-react";
import { InteractionType } from "@azure/msal-browser";
import { useNavigate } from "react-router-dom";
import { loginRequest, protectedResources } from "../authConfig";
import useAuthToken from "../hooks/useAuthToken";
import { createApiClient } from "../utils/apiClient";
import { useUser } from "../utils/userContext";
import { useTier } from "../utils/tierContext";
import { isOwner as checkIsOwner } from "../utils/access";
import UpgradeModal from "./UpgradeModal";
import Toast from "./Toast";
import type { ResolvedFeature, TierLevel } from "../types/UserTypes";
import styles from "./Features.module.css";

interface AddonRequestDetail {
  id: string;
  featureKey: string | null;
  description: string | null;
  quantity: number;
  urgency: string | null;
  status: string | null;
  requestedBy: number;
  reviewedBy: number | null;
  reviewNotes: string | null;
  createdAt: string;
  reviewedAt: string | null;
}

interface CreateAddonRequestModel {
  featureKey: string;
  description: string;
  quantity: number;
  urgency: string;
}

const ADDON_FEATURE_OPTIONS: { value: string; label: string }[] = [
  { value: "custom_report_templates", label: "Custom Report Templates" },
  { value: "integrations", label: "Integrations" },
];

const URGENCY_OPTIONS: { value: string; label: string }[] = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];

const CATEGORY_ORDER = [
  "core_workflow",
  "billing",
  "reporting",
  "admin",
  "integration",
];

const CATEGORY_LABELS: Record<string, string> = {
  core_workflow: "Core Workflow",
  billing: "Billing",
  reporting: "Reporting",
  admin: "Admin",
  integration: "Integration",
};

const Features = () => {
  const authRequest = { ...loginRequest };
  const { token, getToken } = useAuthToken();
  const { user, activeTenantId } = useUser();
  const navigate = useNavigate();
  const { tier, features, loading, error, refreshTier } =
    useTier();

  const ownerUser = checkIsOwner(Number(user?.roleId));

  const [toggling, setToggling] = useState<string | null>(null);
  const [upgradeTarget, setUpgradeTarget] = useState<ResolvedFeature | null>(
    null
  );
  const [addonRequests, setAddonRequests] = useState<AddonRequestDetail[]>([]);
  const [showAddonForm, setShowAddonForm] = useState(false);
  const [submittingAddon, setSubmittingAddon] = useState(false);
  const [addonForm, setAddonForm] = useState<CreateAddonRequestModel>({
    featureKey: ADDON_FEATURE_OPTIONS[0].value,
    description: "",
    quantity: 1,
    urgency: "medium",
  });
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);

  const api = useMemo(() => {
    if (!token || !activeTenantId || !user?.userId) return null;
    return createApiClient({
      baseUrl: protectedResources.baseURL.endpoint,
      token,
      getToken,
      tenantId: activeTenantId,
      userId: user.userId,
    });
  }, [token, getToken, activeTenantId, user?.userId]);

  const handleToggle = useCallback(
    async (feature: ResolvedFeature) => {
      if (!api || toggling) return;
      setToggling(feature.key);
      try {
        await api.put(`subscription/features/${feature.key}`, {
          disabled: !feature.adminDisabled,
        });
        await refreshTier();
        setToast({ message: "Feature updated.", type: "success" });
      } catch (err: any) {
        console.error("Toggle failed:", err);
        const msg = err?.details?.message || err?.message || "Failed to update feature.";
        setToast({ message: msg, type: "error" });
      } finally {
        setToggling(null);
      }
    },
    [api, toggling, refreshTier]
  );

  const handleUpgrade = useCallback(
    async (targetTier: TierLevel) => {
      if (!ownerUser) {
        setToast({
          message: "Only the account Owner can change plans.",
          type: "error",
        });
        return;
      }
      navigate(`/settings/billing?tier=${targetTier}&checkout=start`);
    },
    [navigate, ownerUser]
  );

  // Fetch add-on requests
  const fetchAddonRequests = useCallback(async () => {
    if (!api || !ownerUser) return;
    try {
      const data = await api.get<AddonRequestDetail[]>(
        `subscription/addon-requests`
      );
      setAddonRequests(data ?? []);
    } catch {
      setAddonRequests([]);
    }
  }, [api, ownerUser]);

  useEffect(() => {
    fetchAddonRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, activeTenantId]);

  const handleAddonSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!api || submittingAddon) return;
      setSubmittingAddon(true);
      try {
        await api.post(`subscription/addon-request`, addonForm);
        setAddonForm({
          featureKey: ADDON_FEATURE_OPTIONS[0].value,
          description: "",
          quantity: 1,
          urgency: "medium",
        });
        setShowAddonForm(false);
        await fetchAddonRequests();
        setToast({ message: "Request submitted.", type: "success" });
      } catch (err) {
        console.error("Add-on request failed:", err);
        setToast({ message: "Failed to submit request.", type: "error" });
      } finally {
        setSubmittingAddon(false);
      }
    },
    [api, submittingAddon, addonForm, fetchAddonRequests]
  );

  // Group features by category
  const grouped = CATEGORY_ORDER.map((cat) => ({
    category: cat,
    label: CATEGORY_LABELS[cat] ?? cat,
    items: features.filter((f) => f.category === cat),
  })).filter((g) => g.items.length > 0);

  return (
    <MsalAuthenticationTemplate
      interactionType={InteractionType.Redirect}
      authenticationRequest={authRequest}
    >
        <div className={styles["page-shell"]}>
              {/* Error banner */}
              {error && (
                <div className={styles["error-banner"]}>
                  Unable to load feature settings. Some features may be
                  unavailable.
                </div>
              )}

              {loading ? (
                <div className={styles["loading-container"]}>Loading…</div>
              ) : (
                <>
                  {/* Feature categories */}
                  {grouped.map((group) => (
                    <div
                      key={group.category}
                      className={styles["feat-card"]}
                    >
                      <div className={styles["feat-card-header"]}>
                        <span className={styles["feat-card-header-title"]}>
                          {group.label}
                        </span>
                      </div>
                      <div className={styles["feat-card-body-flush"]}>
                        {group.items.map((feat) => (
                          <FeatureRow
                            key={feat.key}
                            feature={feat}
                            currentTier={tier}
                            toggling={toggling === feat.key}
                            canToggle={ownerUser}
                            onToggle={() => handleToggle(feat)}
                            onUpgrade={() => setUpgradeTarget(feat)}
                          />
                        ))}
                      </div>
                    </div>
                  ))}

                  {/* Add-on Requests */}
                  <div className={styles["feat-card"]}>
                    <div className={styles["feat-card-header"]}>
                      <span className={styles["feat-card-header-title"]}>
                        Add-on Requests
                      </span>
                      {ownerUser && (
                      <button
                        type="button"
                        className={styles["addon-request-btn"]}
                        onClick={() => setShowAddonForm((v) => !v)}
                      >
                        {showAddonForm ? "Cancel" : "Request Add-on"}
                      </button>
                      )}
                    </div>

                    <div className={styles["addon-section"]}>
                    {!ownerUser && (
                      <div style={{ padding: '12px 16px', fontSize: 13, color: '#64748b' }}>
                        Add-on requests can only be submitted and viewed by the account Owner.
                      </div>
                    )}
                    {ownerUser && showAddonForm && (
                      <form
                        className={styles["addon-form"]}
                        onSubmit={handleAddonSubmit}
                      >
                        <div className={styles["addon-form-field"]}>
                          <label className={styles["addon-form-label"]} htmlFor="addon-feature">
                            Feature
                          </label>
                          <select
                            id="addon-feature"
                            className={styles["addon-form-select"]}
                            value={addonForm.featureKey}
                            onChange={(e) =>
                              setAddonForm((f) => ({
                                ...f,
                                featureKey: e.target.value,
                              }))
                            }
                          >
                            {ADDON_FEATURE_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className={styles["addon-form-field"]}>
                          <label className={styles["addon-form-label"]} htmlFor="addon-description">
                            Description
                          </label>
                          <textarea
                            id="addon-description"
                            className={styles["addon-form-textarea"]}
                            value={addonForm.description}
                            onChange={(e) =>
                              setAddonForm((f) => ({
                                ...f,
                                description: e.target.value,
                              }))
                            }
                            rows={3}
                            placeholder="Describe what you need…"
                          />
                        </div>
                        <div className={styles["addon-form-row"]}>
                          <div className={styles["addon-form-field"]}>
                            <label className={styles["addon-form-label"]} htmlFor="addon-quantity">
                              Quantity
                            </label>
                            <input
                              id="addon-quantity"
                              type="number"
                              className={styles["addon-form-input"]}
                              value={addonForm.quantity}
                              min={1}
                              onChange={(e) =>
                                setAddonForm((f) => ({
                                  ...f,
                                  quantity: Math.max(
                                    1,
                                    parseInt(e.target.value, 10) || 1
                                  ),
                                }))
                              }
                            />
                          </div>
                          <div className={styles["addon-form-field"]}>
                            <label className={styles["addon-form-label"]} htmlFor="addon-urgency">
                              Urgency
                            </label>
                            <select
                              id="addon-urgency"
                              className={styles["addon-form-select"]}
                              value={addonForm.urgency}
                              onChange={(e) =>
                                setAddonForm((f) => ({
                                  ...f,
                                  urgency: e.target.value,
                                }))
                              }
                            >
                              {URGENCY_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <button
                          type="submit"
                          className={styles["addon-submit-btn"]}
                          disabled={submittingAddon}
                        >
                          {submittingAddon
                            ? "Submitting…"
                            : "Submit Request"}
                        </button>
                      </form>
                    )}

                    {ownerUser && addonRequests.length > 0 && (
                      <div className={styles["addon-list"]}>
                        {addonRequests.map((req) => (
                          <div key={req.id} className={styles["addon-item"]} data-testid={`addon-request-${req.id}`}>
                            <div className={styles["addon-item-top"]}>
                              <span className={styles["addon-feature-key"]}>
                                {ADDON_FEATURE_OPTIONS.find(
                                  (o) => o.value === req.featureKey
                                )?.label ?? req.featureKey}
                              </span>
                              <div className={styles["addon-badges"]}>
                                <span
                                  className={`${styles["addon-urgency-badge"]} ${
                                    styles[
                                      `addon-urgency-${req.urgency ?? "medium"}`
                                    ] ?? ""
                                  }`}
                                >
                                  {req.urgency ?? "medium"}
                                </span>
                                <span
                                  className={`${styles["addon-status-badge"]} ${
                                    styles[
                                      `addon-status-${req.status ?? "pending_review"}`
                                    ] ?? ""
                                  }`}
                                >
                                  {(req.status ?? "pending_review").replace(
                                    /_/g,
                                    " "
                                  )}
                                </span>
                              </div>
                            </div>
                            {req.description && (
                              <div className={styles["addon-item-desc"]}>
                                {req.description}
                              </div>
                            )}
                            <div className={styles["addon-item-meta"]}>
                              Qty: {req.quantity} · Requested{" "}
                              {new Date(req.createdAt).toLocaleDateString()}
                              {req.reviewNotes && (
                                <span>
                                  {" "}
                                  · Review: {req.reviewNotes}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {ownerUser && addonRequests.length === 0 && !showAddonForm && (
                      <div className={styles["addon-empty"]}>
                        No add-on requests yet.
                      </div>
                    )}
                    </div>
                  </div>
                </>
              )}
          </div>

      {/* Upgrade Modal */}
      {upgradeTarget && upgradeTarget.requiredTier && (
        <UpgradeModal
          featureName={upgradeTarget.displayName}
          requiredTier={upgradeTarget.requiredTier}
          currentTier={tier}
          onUpgrade={handleUpgrade}
          canUpgrade={ownerUser}
          upgradeActionLabel="Continue to Billing"
          onClose={() => setUpgradeTarget(null)}
        />
      )}
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
    </MsalAuthenticationTemplate>
  );
};

// Individual feature row
interface FeatureRowProps {
  feature: ResolvedFeature;
  currentTier: TierLevel;
  toggling: boolean;
  canToggle: boolean;
  onToggle: () => void;
  onUpgrade: () => void;
}

const FeatureRow = ({
  feature,
  toggling,
  canToggle,
  onToggle,
  onUpgrade,
}: FeatureRowProps) => {
  const isLocked = !feature.tierIncluded;
  const isDisabled = feature.adminDisabled;

  const rowClass = [
    styles["feature-row"],
    isDisabled && !isLocked ? styles["feature-row-disabled"] : "",
    isLocked ? styles["feature-row-locked"] : "",
  ]
    .filter(Boolean)
    .join(" ");

  const formatTier = (t: string) => t.replace(/_/g, " ");

  if (isLocked) {
    return (
      <div
        className={rowClass}
        onClick={onUpgrade}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onUpgrade(); } }}
        data-testid={`feature-row-${feature.key}`}
      >
        <div className={styles["feature-left"]}>
          <span className={styles["feature-icon"]}>
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none"><rect x="5" y="9" width="10" height="7.5" rx="2" stroke="#f59e0b" strokeWidth="1.5" fill="none"/><path d="M7.5 9V7a2.5 2.5 0 0 1 5 0v2" stroke="#f59e0b" strokeWidth="1.5" strokeLinecap="round"/><circle cx="10" cy="13" r="1.2" fill="#f59e0b"/><path d="M10 13.8v1.2" stroke="#f59e0b" strokeWidth="1.3" strokeLinecap="round"/></svg>
          </span>
          <div>
            <div className={styles["feature-name"]}>
              {feature.displayName}
              {feature.level && (
                <span className={styles["feature-level"]}>{feature.level}</span>
              )}
            </div>
            {feature.description && (
              <div className={styles["feature-desc"]}>
                {feature.description}
              </div>
            )}
          </div>
        </div>
        <div className={styles["feature-right"]}>
          {feature.requiredTier && (
            <span className={styles["tier-badge"]}>
              {formatTier(feature.requiredTier)}
            </span>
          )}
          <button
            type="button"
            className={styles["upgrade-btn"]}
            onClick={(e) => {
              e.stopPropagation();
              onUpgrade();
            }}
            aria-label={`Upgrade for ${feature.displayName}`}
          >
            Upgrade →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={rowClass} data-testid={`feature-row-${feature.key}`}>
      <div className={styles["feature-left"]}>
        <span className={styles["feature-icon"]}>
          {feature.enabled ? (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><rect width="20" height="20" rx="6" fill="#dcfce7"/><path d="M6 10l3 3 5-5" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><rect x="1" y="1" width="18" height="18" rx="5" stroke="#d1d5db" strokeWidth="1.5"/></svg>
          )}
        </span>
        <div>
          <div className={styles["feature-name"]}>
            {feature.displayName}
            {feature.level && (
              <span className={styles["feature-level"]}>{feature.level}</span>
            )}
          </div>
          {feature.description && (
            <div className={styles["feature-desc"]}>{feature.description}</div>
          )}
        </div>
      </div>
      <div className={styles["feature-right"]}>
        {isDisabled && (
          <span className={styles["admin-disabled-note"]}>
            Disabled by account Owner
          </span>
        )}
        {canToggle && (
        <button
          type="button"
          className={`${styles.toggle} ${
            feature.enabled ? styles["toggle-on"] : styles["toggle-off"]
          }`}
          onClick={onToggle}
          disabled={toggling}
          aria-label={`Toggle ${feature.displayName}`}
        >
          <span className={styles["toggle-knob"]} />
        </button>
        )}
      </div>
    </div>
  );
};

export default Features;
